<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>
        Pending Payouts for Salesperson<span v-if="participantName">: {{ participantName }}</span>
        <span v-else-if="loading">: Loading...</span>
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        icon
        title="Export CSV"
        aria-label="Export CSV"
        :disabled="loading || loadError || isExporting || !items.length"
        @click="exportDisplayedData"
      >
        <v-icon v-if="!isExporting">mdi-download</v-icon>
        <v-progress-circular v-else indeterminate color="primary" size="20" width="2" />
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-alert v-if="exportError" type="error" text>{{ exportError }}</v-alert>
      <v-alert v-if="loadError" type="error" text>
        Unable to load salesperson payout details. Please try again.
      </v-alert>
      <v-data-table
        v-else
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :no-data-text="noPayoutsMessage"
      >
        <template v-slot:item.createdDate="{ item }">
          {{ item.createdDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
        </template>
        <template v-slot:item.courseInfo="{ item }">
          <span v-if="item.promotion?.coursesEnabled && item.totalCourses !== undefined">
            {{ item.approvedCourses }} / {{ item.totalCourses }}
          </span>
          <span v-else-if="item.promotion?.coursesEnabled"> Unavailable </span>
          <span v-else> N/A </span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "SalespersonDetails",
  data() {
    return {
      loading: true,
      isExporting: false,
      exportError: "",
      dealerName: "",
      participant: null,
      loadError: false,
      activeCoursesCache: {},
      items: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortDesc: [true]
      },
      headers: [
        { text: "Date", value: "createdDate" },
        { text: "Promotion", value: "promotion.name" },
        { text: "Model", value: "saleProduct.product.productKey", sortable: false },
        { text: "VIN", value: "saleProduct.serialNumber", sortable: false },
        { text: "Award Vehicle", value: "awardVehicle.name" },
        { text: "Amount", value: "payoutAmount" },
        { text: "Completed Course Info", value: "courseInfo" }
      ]
    };
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient", "selectedFiscalYear"]),
    participantName() {
      return (
        this.participant?.fullName ||
        [this.participant?.participantFirstName, this.participant?.participantLastName].filter(Boolean).join(" ")
      );
    },
    noPayoutsMessage() {
      return !this.loading && !this.loadError && !this.items.length
        ? "No pending payouts for the selected period."
        : "";
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    }
  },
  methods: {
    getPayoutFilters() {
      const filters = {
        participant: { id: this.$route.query.salespersonId },
        payoutStatus: "pending"
      };

      if (this.selectedFiscalYear?.fiscalYearStart) {
        filters.createdAfter = this.selectedFiscalYear.fiscalYearStart;
      }
      if (this.selectedFiscalYear?.fiscalYearEnd) {
        filters.createdBefore = this.$util
          .getDateInClientTimezone(this.selectedFiscalYear.fiscalYearEnd)
          .add(1, "day")
          .toDate();
      }

      return filters;
    },
    async fetchData() {
      this.loading = true;
      this.loadError = false;
      this.participant = null;
      this.items = [];
      this.total = 0;

      const filters = this.getPayoutFilters();
      try {
        const params = this.$util.buildPaginationParams(this.options);
        params.append("expand", "awardVehicle");
        params.append("expand", "promotion");
        params.append("expand", "participant");
        params.append("expand", "saleProduct.product");

        const { data } = await this.$api.post(`/api/payouts/search?${params.toString()}`, filters);
        const payouts = data.content;

        this.items = payouts;
        this.total = data.totalElements;

        this.participant = payouts[0]?.participant || null;

        await this.setCourseProgressToPayouts(payouts);
      } catch {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async setCourseProgressToPayouts(payouts) {
      await Promise.all(payouts.map(payout => this.setCourseProgressToPayout(payout)));
    },
    async setCourseProgressToPayout(payout) {
      if (!payout.promotion?.coursesEnabled) return;

      try {
        const courses = await this.fetchActiveCourses(payout.promotion);
        if (!courses.length) return;

        const approvedCourses = await this.fetchApprovedCourses(payout.participant.id, courses);

        this.$set(payout, "totalCourses", courses.length);
        this.$set(payout, "approvedCourses", approvedCourses.length);
      } catch {
        // Course progress is optional; the table renders Unavailable when it cannot load.
      }
    },
    async fetchActiveCourses(promotion) {
      const cacheKey = promotion.id;
      if (this.activeCoursesCache[cacheKey] !== undefined) return this.activeCoursesCache[cacheKey];

      const url = "/api/promotionCourses/search";
      const payload = { promotion: { id: promotion.id }, effective: true };

      this.activeCoursesCache[cacheKey] = this.$util.fetchPaginatedData(url, payload).catch(error => {
        delete this.activeCoursesCache[cacheKey];
        throw error;
      });
      return this.activeCoursesCache[cacheKey];
    },

    async fetchApprovedCourses(participantId, courses) {
      if (!courses.length) return [];

      const url = "/api/courseGrades/search";
      const payload = {
        participant: { id: participantId },
        courses: courses.map(promotionCourse => ({ id: promotionCourse.course.id }))
      };
      const courseGrades = await this.$util.fetchPaginatedData(url, payload);

      return courseGrades.filter(courseGrade => courseGrade.completedDate);
    },
    async exportDisplayedData() {
      this.isExporting = true;
      this.exportError = "";
      try {
        const payouts = await this.$util.fetchPaginatedData("/api/payouts/search", this.getPayoutFilters(), 100, [
          "awardVehicle",
          "promotion",
          "participant",
          "saleProduct.product"
        ]);
        await this.setCourseProgressToPayouts(payouts);
        const columns = this.headers.map(header => {
          if (header.value === "createdDate") {
            return {
              ...header,
              format: row => this.$util.formatDateClient(row.createdDate, "MM/DD/YYYY", this.selectedClient)
            };
          }
          if (header.value === "courseInfo") {
            return {
              ...header,
              format: row => {
                if (!row.promotion?.coursesEnabled) return "N/A";
                // Keep the course count as text when Excel opens the CSV.
                return row.totalCourses !== undefined
                  ? `="${row.approvedCourses} / ${row.totalCourses}"`
                  : "Unavailable";
              }
            };
          }
          return header;
        });
        this.$export.exportToCsv(
          `${this.participant?.participantKey || this.$route.query.salespersonId}_pending_payouts`,
          columns,
          payouts
        );
      } catch (error) {
        this.exportError =
          this.$kawasaki.getApiErrorMessages(error?.response?.data)[0] ||
          error?.message ||
          "Unable to export the report. Please try again.";
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>
